import { useMemo } from "react";

import {
  Paper,
  Radio,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { t } from "i18next";
import { textAlign } from "@mui/system";

const StrippedTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "rgba(158, 156, 156, 0.1)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "#FAFCFF",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgba(158, 156, 156, 0.1)",
    color: theme.palette.common.black,
    borderBottom: "none",
    fontSize: "14px",
    textAlign: "center",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },
}));

// Renders the first cell of a buy-down row: the radio-selectable interest
// rate. Pulled out of the tableBody.map/Object.keys(body).map nesting inside
// the useMemo callback so the onChange handler isn't a function nested 5
// levels deep (component > useMemo > row map > cell map > onChange).
function BuyDownRateCell({
  cellKey,
  index,
  value,
  isSelected,
  onSelect,
}: {
  cellKey: string;
  index: number;
  value: any;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <StyledTableCell
      style={{
        color: "black",
        borderBottom: "none",
        marginBottom: "8px",
        textAlign: "center",
        fontWeight: isSelected ? 700 : 500,
      }}
      key={`${cellKey}-${index}`}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <Radio checked={isSelected} onChange={onSelect} />
        <span>{value}</span>
      </div>
    </StyledTableCell>
  );
}

// Renders every non-first cell of a buy-down row (plain value display).
function BuyDownValueCell({
  cellKey,
  index,
  value,
  isHighlighted,
}: {
  cellKey: string;
  index: number;
  value: any;
  isHighlighted: boolean;
}) {
  return (
    <StyledTableCell
      style={{
        color: "black",
        borderBottom: "none",
        marginBottom: "8px",
        borderLeft: "1px solid #D5DCED",
        textAlign: "center",
        fontWeight: isHighlighted ? 700 : 500,
      }}
      key={`${cellKey}-${index}`}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span>{value}</span>
      </div>
    </StyledTableCell>
  );
}

// Renders all cells of a single buy-down row. Kept as its own top-level
// function (rather than inline inside the row map) so the per-cell onSelect
// handler isn't nested 5 levels deep (component > useMemo > row map > cell
// map > onSelect) — as a standalone function its own deepest nesting is only
// function > map > ternary-branch.
function renderBuyDownRowCells(
  body: any,
  index: number,
  bIdx: number | null,
  currentSelectedInterestRate: any,
  handleInterestChange: (body: any) => void,
) {
  return Object.keys(body).map((key, idx) =>
    idx === 0 ? (
      <BuyDownRateCell
        key={`${key}-${index}`}
        cellKey={key}
        index={index}
        value={body[key]}
        isSelected={currentSelectedInterestRate?.interestRate === body[key]}
        onSelect={() => handleInterestChange(body)}
      />
    ) : (
      <BuyDownValueCell
        key={`${key}-${index}`}
        cellKey={key}
        index={index}
        value={body[key]}
        isHighlighted={bIdx === index}
      />
    ),
  );
}

export default function BuyDownOptionsTable(props: any) {
  const {
    tableHeader,
    tableBody,
    label,
    tableClassName,
    classes,
    currentSelectedInterestRate,
    handleInterestChange,
  } = props;

  const renderTableBody = useMemo(() => {
    if (!tableBody) return null;
    let bIdx: number | null = null;
    tableBody?.find((tB: any, idx: number) => {
      if (tB?.interestRate === currentSelectedInterestRate?.interestRate)
        bIdx = idx;
    });

    return tableBody.map((body: any, index: number) => (
      <StrippedTableRow key={`Row-${index}`}>
        {renderBuyDownRowCells(body, index, bIdx, currentSelectedInterestRate, handleInterestChange)}
      </StrippedTableRow>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableBody, t, label, currentSelectedInterestRate]);

  return (
    // <Paper className={classes?.paper}>
    <TableContainer
      sx={{
        padding: "0px 0px",
        borderRadius: "8px",
        fontSize: "1rem",
      }}
    >
      <Table className={tableClassName}>
        {tableHeader?.length ? (
          <TableHead
            style={{
              backgroundColor: "#9E9C9C1A",
              fontSize: "14px",
            }}
          >
            <StrippedTableRow style={{ borderRadius: "8px" }}>
              {tableHeader?.map((ele: any, idx: number) => (
                <StyledTableCell
                  className={ele?.headerClassName ?? ""}
                  key={`${ele?.label}-${idx}`}
                >
                  {t(ele?.label ?? ele)}
                </StyledTableCell>
              ))}
            </StrippedTableRow>
          </TableHead>
        ) : null}
        <TableBody>{renderTableBody}</TableBody>
      </Table>
    </TableContainer>
    // </Paper>
  );
}
